<template>
  <div
    ref="frame"
    class="rpc"
    :class="[`rpc--${resolvedDirection}`, `rpc--${resolvedMode}`]"
    :style="rootStyle"
  >
    <template v-if="photoCount > 1">
      <div
        ref="reelTrack"
        :key="reelTrackKey"
        class="rpc__reel-track"
        :class="{
          'rpc__reel-track--continuous': resolvedMode === 'filmstrip',
          'rpc__reel-track--stepped': resolvedMode === 'carousel',
        }"
        :style="reelTrackStyle"
        @transitionend="onReelTransitionEnd"
      >
        <div
          v-for="copy in reelCopyCount"
          :key="`reel-copy-${copy}`"
          class="rpc__reel-set"
          :aria-hidden="copy > 1 ? 'true' : undefined"
        >
          <div
            v-for="(url, photoIndex) in photos"
            :key="`reel-${copy}-${photoIndex}`"
            class="rpc__reel-slide"
            :style="reelSlideStyle(url)"
          >
            <img
              class="rpc__img"
              :src="url"
              alt=""
              draggable="false"
              @load="recordPhotoAspectRatio(url, $event)"
              @error="recordPhotoAspectRatio(url, $event)"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="photoCount === 1">
      <div class="rpc__static-photo">
        <img
          class="rpc__img"
          :src="photos[0]"
          alt=""
          draggable="false"
          @load="recordPhotoAspectRatio(photos[0], $event)"
          @error="recordPhotoAspectRatio(photos[0], $event)"
        />
      </div>
    </template>
  </div>
</template>

<script>
import {
  RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_DEFAULT,
  RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MAX,
  RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MIN,
  RECENT_PHOTO_IMAGE_SPACING_DEFAULT,
  RECENT_PHOTO_IMAGE_SPACING_MAX,
  RECENT_PHOTO_IMAGE_SPACING_MIN,
  RECENT_PHOTO_TRANSITION_MS,
  normalizeRecentPhotoDisplayMode,
  normalizeRecentPhotoDirection,
  normalizeRecentPhotoFilmstripVisibleCount,
  normalizeRecentPhotoImageSpacing,
  normalizeRecentPhotoSpeed,
} from '@/utils/recent_photo_carousel';

export default {
  name: 'Recent_Photo_Carousel',
  props: {
    // Absolute image URLs, in display order.
    photos: {
      type: Array,
      default: () => [],
    },
    // Seconds each photo is held.
    speed: {
      type: Number,
      default: undefined,
    },
    direction: {
      type: String,
      default: 'horizontal',
      validator: (value) => value === 'horizontal' || value === 'vertical',
    },
    mode: {
      type: String,
      default: 'carousel',
      validator: (value) => value === 'carousel' || value === 'filmstrip',
    },
    visibleCount: {
      type: Number,
      default: RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_DEFAULT,
      validator: (value) =>
        Number.isInteger(value) &&
        value >= RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MIN &&
        value <= RECENT_PHOTO_FILMSTRIP_VISIBLE_COUNT_MAX,
    },
    spacing: {
      type: Number,
      default: RECENT_PHOTO_IMAGE_SPACING_DEFAULT,
      validator: (value) =>
        Number.isInteger(value) &&
        value >= RECENT_PHOTO_IMAGE_SPACING_MIN &&
        value <= RECENT_PHOTO_IMAGE_SPACING_MAX,
    },
  },
  data() {
    return {
      index: 0,
      // Turned off for the silent stepped-mode jump back to the first copy.
      animating: true,
      stepInFlight: false,
      frameWidth: 0,
      frameHeight: 0,
      photoAspectRatios: {},
    };
  },
  computed: {
    photoCount() {
      return this.photos.length;
    },
    dwellMs() {
      return normalizeRecentPhotoSpeed(this.speed) * 1000;
    },
    resolvedDirection() {
      return normalizeRecentPhotoDirection(this.direction);
    },
    resolvedMode() {
      return normalizeRecentPhotoDisplayMode(this.mode);
    },
    resolvedVisibleCount() {
      return normalizeRecentPhotoFilmstripVisibleCount(this.visibleCount);
    },
    resolvedSpacing() {
      return normalizeRecentPhotoImageSpacing(this.spacing);
    },
    effectiveVisibleCount() {
      return Math.min(this.resolvedVisibleCount, Math.max(this.photoCount, 1));
    },
    resolvedPhotoAspectRatios() {
      return this.photos.map((url) => {
        const ratio = Number(this.photoAspectRatios[String(url || '')]);
        return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
      });
    },
    uniformPhotoHeight() {
      const frameWidth = Math.max(Number(this.frameWidth) || 0, 0);
      const frameHeight = Math.max(Number(this.frameHeight) || 0, 0);
      if (!frameWidth || !frameHeight) return 0;

      const ratios = this.resolvedPhotoAspectRatios.length
        ? this.resolvedPhotoAspectRatios
        : [1];
      const maxRatio = Math.max(...ratios, 1);
      const spacing = this.resolvedSpacing;

      // A single image is centered at the largest uncropped size the artboard
      // permits. Spacing belongs between photos, so it never shrinks this case.
      if (this.photoCount <= 1) {
        return Math.max(1, Math.min(frameHeight, frameWidth / maxRatio));
      }

      const visibleCount = this.effectiveVisibleCount;
      const widthSafeHeight = frameWidth / maxRatio;

      if (this.resolvedDirection === 'vertical') {
        const availableHeight = Math.max(frameHeight - spacing * (visibleCount - 1), 1);
        return Math.max(1, Math.min(availableHeight / visibleCount, widthSafeHeight));
      }

      // Horizontal photos retain their natural widths. The selected visible
      // count therefore controls the row's density, while a single shared
      // height keeps every landscape and portrait photo perfectly aligned.
      const averageRatio = ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;
      const availableWidth = Math.max(frameWidth - spacing * (visibleCount - 1), 1);
      const densityHeight = availableWidth / (visibleCount * averageRatio);
      return Math.max(1, Math.min(frameHeight, widthSafeHeight, densityHeight));
    },
    rootStyle() {
      return {
        '--rpc-image-spacing': `${this.resolvedSpacing}px`,
        '--rpc-uniform-photo-height': this.uniformPhotoHeight
          ? `${this.uniformPhotoHeight}px`
          : '100%',
      };
    },
    reelLoopDistance() {
      if (this.photoCount < 1 || !this.uniformPhotoHeight) return 0;
      const photoLength = this.resolvedDirection === 'vertical'
        ? this.photoCount * this.uniformPhotoHeight
        : this.resolvedPhotoAspectRatios.reduce(
            (total, ratio) => total + ratio * this.uniformPhotoHeight,
            0
          );
      // Each repeated set owns its trailing seam gap. That makes the distance
      // from the last photo to the next first photo identical to every other
      // configured gap.
      return photoLength + this.photoCount * this.resolvedSpacing;
    },
    reelCopyCount() {
      if (this.photoCount < 2 || this.reelLoopDistance <= 0) return 2;
      const frameLength = this.resolvedDirection === 'vertical'
        ? this.frameHeight
        : this.frameWidth;
      // Very wide/short source photos can make one vertical set shorter than
      // the frame. Extra identical copies guarantee that neither mode ever
      // reveals empty space while crossing the seam.
      return Math.max(2, Math.ceil(frameLength / this.reelLoopDistance) + 2);
    },
    steppedOffset() {
      const steps = Math.min(Math.max(this.index, 0), this.photoCount);
      if (!steps || !this.uniformPhotoHeight) return 0;
      if (this.resolvedDirection === 'vertical') {
        return steps * (this.uniformPhotoHeight + this.resolvedSpacing);
      }
      const photoWidth = this.resolvedPhotoAspectRatios
        .slice(0, steps)
        .reduce((total, ratio) => total + ratio * this.uniformPhotoHeight, 0);
      return photoWidth + steps * this.resolvedSpacing;
    },
    reelTrackStyle() {
      const common = {
        '--rpc-loop-offset': `${-Math.max(this.reelLoopDistance, 0)}px`,
        width: this.resolvedDirection === 'vertical' ? '100%' : 'max-content',
        height: this.resolvedDirection === 'vertical' ? 'max-content' : '100%',
      };
      if (this.resolvedMode === 'filmstrip') {
        return {
          ...common,
          animationDuration: `${Math.max(this.photoCount, 1) * this.dwellMs}ms`,
        };
      }
      const transform = this.resolvedDirection === 'vertical'
        ? `translate3d(0, -${this.steppedOffset}px, 0)`
        : `translate3d(-${this.steppedOffset}px, 0, 0)`;
      return {
        ...common,
        transform,
        transitionDuration: this.animating ? `${RECENT_PHOTO_TRANSITION_MS}ms` : '0ms',
      };
    },
    reelGeometryKey() {
      return [
        this.resolvedDirection,
        this.resolvedVisibleCount,
        this.resolvedSpacing,
        Math.round(this.uniformPhotoHeight * 100) / 100,
        ...this.resolvedPhotoAspectRatios.map((ratio) => Math.round(ratio * 10000) / 10000),
        ...this.photos.map((url) => String(url || '')),
      ].join('|');
    },
    reelTrackKey() {
      return this.resolvedMode === 'filmstrip'
        ? `continuous|${this.reelGeometryKey}|${this.dwellMs}`
        : `stepped|${this.reelGeometryKey}`;
    },
  },
  methods: {
    measureFrame() {
      const frame = this.$refs.frame;
      if (!frame) return;
      const rect = frame.getBoundingClientRect();
      // clientWidth/clientHeight are the component's own CSS coordinate space.
      // Prefer them to the transformed bounding box so Dashboard embedding or
      // admin-shell zoom cannot make the calculated image row too small.
      const width = Math.max(frame.clientWidth || rect.width || 0, 0);
      const height = Math.max(frame.clientHeight || rect.height || 0, 0);
      if (Math.abs(width - this.frameWidth) > 0.25) this.frameWidth = width;
      if (Math.abs(height - this.frameHeight) > 0.25) this.frameHeight = height;
    },
    recordPhotoAspectRatio(url, event) {
      const image = event?.target;
      const naturalWidth = Number(image?.naturalWidth) || 0;
      const naturalHeight = Number(image?.naturalHeight) || 0;
      const ratio = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : 1;
      const key = String(url || '');
      if (Math.abs(Number(this.photoAspectRatios[key]) - ratio) < 0.0001) return;
      this.photoAspectRatios = { ...this.photoAspectRatios, [key]: ratio };
    },
    photoAspectRatio(url) {
      const ratio = Number(this.photoAspectRatios[String(url || '')]);
      return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
    },
    reelSlideStyle(url) {
      if (!this.uniformPhotoHeight) return {};
      const height = this.uniformPhotoHeight;
      if (this.resolvedDirection === 'vertical') {
        return {
          flexBasis: `${height}px`,
          height: `${height}px`,
        };
      }
      const width = height * this.photoAspectRatio(url);
      return {
        flexBasis: `${width}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
    start() {
      this.stop();
      if (this.resolvedMode !== 'carousel') return;
      if (this.photoCount < 2) return;
      // The configured duration is a true stationary pause. Scheduling from
      // the end of a transition keeps the movement from consuming that pause.
      this.scheduleNextStep();
    },
    scheduleNextStep() {
      if (this.resolvedMode !== 'carousel' || this.photoCount < 2) return;
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.timer = null;
        this.advance();
      }, this.dwellMs);
    },
    stop() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.clearStepCompletion();
      this.stepInFlight = false;
    },
    advance() {
      if (this.resolvedMode !== 'carousel' || this.photoCount < 2 || this.stepInFlight) {
        return;
      }

      // Never step past the first repeated set. This keeps the offset inside
      // populated content even after a hidden or interrupted transition.
      if (this.index >= this.photoCount) {
        this.snapHome();
        return;
      }

      this.animating = true;
      this.stepInFlight = true;
      this.index += 1;
      this.clearStepCompletion();
      // v-show can suppress transitionend while an artboard is inactive. This
      // fallback completes every step so the sequence can never stall hidden.
      this.transitionTimer = setTimeout(
        this.completeStep,
        RECENT_PHOTO_TRANSITION_MS + 80
      );
    },
    // transitionend is the fast path; the per-step fallback above guarantees
    // completion for temporarily hidden display elements.
    clearStepCompletion() {
      if (this.transitionTimer) {
        clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }
    },
    completeStep() {
      if (!this.stepInFlight) return;
      this.clearStepCompletion();
      this.stepInFlight = false;
      if (this.index === this.photoCount) {
        this.snapHome();
        return;
      }
      this.scheduleNextStep();
    },
    // Swap copy two's first photo for the identical photo in copy one with the
    // transition off, so the visual position never jumps.
    snapHome() {
      this.clearStepCompletion();
      this.stepInFlight = false;
      if (this.index < this.photoCount) {
        this.scheduleNextStep();
        return;
      }
      this.animating = false;
      this.index = 0;
      // Re-enabled a frame later; doing it synchronously would let the browser
      // collapse both changes into one animated jump backwards.
      if (this.reanimateFrame) cancelAnimationFrame(this.reanimateFrame);
      this.reanimateFrame = requestAnimationFrame(() => {
        this.reanimateFrame = requestAnimationFrame(() => {
          this.reanimateFrame = null;
          this.animating = true;
          this.scheduleNextStep();
        });
      });
    },
    onReelTransitionEnd(event) {
      if (
        this.resolvedMode !== 'carousel' ||
        event?.target !== this.$refs.reelTrack ||
        event?.propertyName !== 'transform'
      ) {
        return;
      }
      this.completeStep();
    },
    // Any change to the strip or its timing restarts cleanly rather than
    // leaving the index pointing past the end of a shorter list.
    reset() {
      this.stop();
      if (this.reanimateFrame) {
        cancelAnimationFrame(this.reanimateFrame);
        this.reanimateFrame = null;
      }
      this.animating = false;
      this.index = 0;
      this.$nextTick(() => {
        this.animating = true;
        this.start();
      });
    },
  },
  watch: {
    reelGeometryKey() {
      this.reset();
    },
    resolvedMode() {
      this.reset();
    },
    dwellMs() {
      this.reset();
    },
  },
  mounted() {
    this.$nextTick(this.measureFrame);
    if (typeof ResizeObserver !== 'undefined') {
      this.frameResizeObserver = new ResizeObserver(this.measureFrame);
      if (this.$refs.frame) this.frameResizeObserver.observe(this.$refs.frame);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measureFrame);
    }
    this.start();
  },
  beforeUnmount() {
    this.stop();
    if (this.reanimateFrame) {
      cancelAnimationFrame(this.reanimateFrame);
      this.reanimateFrame = null;
    }
    if (this.frameResizeObserver) {
      this.frameResizeObserver.disconnect();
      this.frameResizeObserver = null;
    } else if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.measureFrame);
    }
  },
};
</script>

<style scoped>
/* Height resolves two ways on purpose.
 *
 * Where an ancestor has a definite height (the settings modal's preview box)
 * `height: 100%` wins and the carousel fills it. On the display the element
 * track is content-driven — exactly like a Poster, which is just an image at
 * `max-width: 100%` — so `height: 100%` computes to `auto` and `aspect-ratio`
 * supplies the height instead. Without that fallback the slides, which are all
 * `height: 100%`, would collapse to nothing and the carousel would render
 * blank. Photos are `contain`ed, so the frame's shape never crops them. */
.rpc {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  white-space: normal;
}

/* A vertical strip is given a portrait-ish frame so stacked photos have room. */
.rpc--vertical {
  aspect-ratio: 4 / 3;
}

/* Both modes use identical repeated photo sets. Continuous mode moves by one
   exact set length; stepped mode moves by one exact photo length, rests, then
   repeats. Natural-width cells keep every photo uncropped and every image at
   the same measured height. */
.rpc__reel-track {
  display: flex;
  will-change: transform;
}

.rpc--horizontal .rpc__reel-track {
  flex-direction: row;
}

.rpc--vertical .rpc__reel-track {
  flex-direction: column;
}

.rpc__reel-track--continuous {
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.rpc--horizontal .rpc__reel-track--continuous {
  animation-name: rpc-reel-continuous-horizontal;
}

.rpc--vertical .rpc__reel-track--continuous {
  animation-name: rpc-reel-continuous-vertical;
}

.rpc__reel-track--stepped {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.rpc__reel-set {
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  gap: var(--rpc-image-spacing, 12px);
  box-sizing: border-box;
}

.rpc--horizontal .rpc__reel-set {
  flex-direction: row;
  width: max-content;
  height: 100%;
  align-items: center;
  padding-inline-end: var(--rpc-image-spacing, 12px);
}

.rpc--vertical .rpc__reel-set {
  flex-direction: column;
  width: 100%;
  height: max-content;
  align-items: center;
  padding-block-end: var(--rpc-image-spacing, 12px);
}

.rpc__reel-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.rpc--horizontal .rpc__reel-slide {
  flex: 0 0 auto;
  width: auto;
  height: var(--rpc-uniform-photo-height, 100%);
}

.rpc--vertical .rpc__reel-slide {
  flex: 0 0 auto;
  width: 100%;
  height: var(--rpc-uniform-photo-height, 100%);
}

@keyframes rpc-reel-continuous-horizontal {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(var(--rpc-loop-offset), 0, 0); }
}

@keyframes rpc-reel-continuous-vertical {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, var(--rpc-loop-offset), 0); }
}

.rpc__static-photo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* `contain`, not `cover`: a memorial photo must never be cropped to fill the
   frame. */
.rpc__img {
  display: block;
  max-width: none;
  max-height: none;
  width: auto;
  height: var(--rpc-uniform-photo-height, 100%);
  object-fit: contain;
}

.rpc__reel-slide .rpc__img {
  width: auto;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

.rpc__static-photo .rpc__img {
  width: auto;
  height: var(--rpc-uniform-photo-height, 100%);
  max-width: 100%;
  max-height: 100%;
}

</style>
